import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from './api';
import { BrandHeader } from './Brand';
import { DISCORD_URL } from './config';
import { useI18n } from './i18n';
import SiteFooter from './SiteFooter';
import { trackClick } from './analytics';

const CATEGORIES = ['Question', 'Add my trading platform', 'Special request', 'Suggestion', 'Problem / bug', 'Account or billing', 'Other'];
// Links like /contact?topic=platform open the form on that topic.
const TOPICS = { platform: 'Add my trading platform', request: 'Special request' };
const PLACEHOLDERS = {
  'Add my trading platform': 'Which platform do you use, and which prop firm? We’ll reply by email to get a sample export and add it.',
  'Special request': 'Tell us what would make your journal easier to use.',
};
const emptyForm = { name: '', email: '', category: 'Question', message: '', website: '' };

function ContactPage({ user }) {
  const { t, tr } = useI18n();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState(() => ({ ...emptyForm, category: TOPICS[searchParams.get('topic')] || emptyForm.category }));
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSending(true);
    try {
      await api('/contact', { method: 'POST', body: form });
      setSent(true);
      setForm(emptyForm);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="App">
      <BrandHeader>
        {user ? (
          <Link to="/app" className="header-link">{t('Journal')}</Link>
        ) : (
          <>
            <Link to="/login" className="header-link">{t('Log in')}</Link>
            <Link to="/signup" className="nav-button-small" onClick={() => trackClick('header-signup')}>{t('Sign up free')}</Link>
          </>
        )}
      </BrandHeader>

      <div className="page-title">
        <p className="eyebrow">{t('Help & contact')}</p>
        <h1>{t("We'd love to hear from you.")}</h1>
        <p className="subtitle">{t("We're here to make your trading journey as easy and smooth as possible. Reach out with questions, special requests, a bug to report, or if you'd like us to add your trading platform.")}</p>
      </div>

      <div className={`contact-layout${DISCORD_URL ? '' : ' single'}`}>
        {DISCORD_URL && (
          <section className="contact-card discord-card">
            <h2>{t('Join the community')}</h2>
            <p>
              {t('The fastest way to get help. Ask questions, share ideas for new features, and talk trading with other Market Matrix members on Discord.')}
            </p>
            <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer" className="cta-button">
              {t('Join our Discord')} &rarr;
            </a>
          </section>
        )}

        <section className="contact-card">
          <h2>{t('Send us a message')}</h2>
          {sent ? (
            <div className="contact-sent">
              <p className="success">{t('Thanks, your message has been sent.')}</p>
              <p className="hint">
                {user ? t("We'll reply by email at {email}.", { email: user.email }) : t("We'll reply by email.")}
              </p>
              <button type="button" className="secondary" onClick={() => setSent(false)}>
                {t('Send another message')}
              </button>
            </div>
          ) : (
            <form className="contact-form" onSubmit={handleSubmit}>
              {user ? (
                <p className="hint">
                  {tr("Sending as <0>{name}</0> ({email}). We'll reply to that email.", [(x) => <strong>{x}</strong>], { name: user.name, email: user.email })}
                </p>
              ) : (
                <div className="fields">
                  <label>
                    {t('Name')}
                    <input name="name" value={form.name} onChange={handleChange} autoComplete="name" required />
                  </label>
                  <label>
                    {t('Email')}
                    <input name="email" type="email" value={form.email} onChange={handleChange} autoComplete="email" required />
                  </label>
                </div>
              )}
              <label>
                {t('Topic')}
                <select name="category" value={form.category} onChange={handleChange}>
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>{t(c)}</option>
                  ))}
                </select>
              </label>
              <label>
                {t('Message')}
                <textarea
                  name="message"
                  rows="6"
                  value={form.message}
                  onChange={handleChange}
                  placeholder={t(PLACEHOLDERS[form.category] || 'How can we help?')}
                  minLength={5}
                  maxLength={5000}
                  required
                />
              </label>
              {/* Hidden from people; catches spam bots that fill in every field */}
              <input
                className="hp-field"
                name="website"
                value={form.website}
                onChange={handleChange}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />
              {error && <p className="error">{error}</p>}
              <button type="submit" disabled={sending}>{sending ? t('Sending…') : t('Send message')}</button>
            </form>
          )}
        </section>
      </div>

      <SiteFooter />
    </div>
  );
}

export default ContactPage;
